/**
 * The people of the dataset (product owner, 2026-10-14): who appears in its videos.
 *
 * A video shows one person at most and a person has any number of videos, so a person is one more
 * way of cutting the dataset — listed in its sidebar under the groups, and one more filter of the
 * gallery. Everything the screens decide about them is here: what the sidebar lists, the address,
 * what the dialogs offer and what the toasts say.
 *
 * ## The selection is in the address
 *
 * `?person=<id>`, and `?person=none` for the videos assigned to nobody, like `?group=`. One thing
 * is selected in the sidebar at a time: choosing a person leaves the group, and choosing a group
 * leaves the person. Inside a group, the funnel's «Persona» still narrows to one.
 */
import type { LocationQueryRaw } from 'vue-router';
import type { Person, VideoFilters } from '../model.js';
import { formatCount } from './format-count.js';

/** Spanish order: «Álvaro» with the A's. The API sorts them already; a created one lands in place. */
const COLLATOR = new Intl.Collator('es', { sensitivity: 'base', numeric: true });

/** Upstream's limits: the name's length, and how many videos one request assigns. */
export const PERSON_NAME_MAX = 120;
export const MAX_VIDEOS_PER_ASSIGN = 500;

/** A person, «sin persona», or nothing selected. */
export type PersonSelection = number | 'none' | null;

// ── The address ─────────────────────────────────────────────────────────────

export const DATASET_PERSON_PARAM = 'person';

/** Kept in step with `DATASET_GROUP_PARAM`: one sidebar, one thing selected. */
const GROUP_PARAM = 'group';

/** And with `DATA_VIEW_PARAM`: a person's page is not the persons grid. */
const VIEW_PARAM = 'view';

/** What `?person=` names: a person, `none`, or null for anything else. */
export function datasetPersonFrom(raw: unknown): PersonSelection {
  if (raw === 'none') return 'none';
  if (typeof raw !== 'string' || !/^\d+$/.test(raw)) return null;

  const id = Number(raw);

  return Number.isSafeInteger(id) && id > 0 ? id : null;
}

/** The address with this person selected — and no group — or with no person for null. */
export function withDatasetPerson(
  query: Readonly<LocationQueryRaw>,
  selection: PersonSelection,
): LocationQueryRaw {
  const next: LocationQueryRaw = { ...query };

  if (selection === null) {
    delete next[DATASET_PERSON_PARAM];
    return next;
  }

  delete next[GROUP_PARAM];
  delete next[VIEW_PARAM];
  next[DATASET_PERSON_PARAM] = String(selection);

  return next;
}

// ── The filter ──────────────────────────────────────────────────────────────

/** The value of a person select: `''` is «cualquiera», `'none'` «sin persona», else the id. */
export function personChoiceOf(selection: PersonSelection): string {
  return selection === null ? '' : String(selection);
}

/**
 * The filters narrowed to the person the sidebar has selected, or as they are. The sidebar wins
 * over the funnel's own: upstream takes one person, and what is selected is what the title says.
 */
export function withPerson(filters: VideoFilters, selection: PersonSelection): VideoFilters {
  if (selection === null) return filters;

  return { ...filters, personId: selection === 'none' ? null : selection };
}

// ── The sidebar ─────────────────────────────────────────────────────────────

export interface PersonEntry {
  /** What the list is keyed by: the id, or `none`. */
  key: string;
  selection: Exclude<PersonSelection, null>;
  label: string;
  /** How many videos; null while that is not known. */
  videos: number | null;
  hasCv: boolean;
}

export interface PersonSidebar {
  persons: PersonEntry[];
  /** «Sin persona»: every video assigned to nobody. */
  none: PersonEntry;
}

/**
 * The section from what the screen holds: the people, and the dataset's total — «sin persona» is
 * what is left of it once everybody's videos are taken out, known only when the total is.
 */
export function personSidebar(input: {
  persons: readonly Person[];
  total: number | null;
}): PersonSidebar {
  const assigned = input.persons.reduce((sum, person) => sum + person.videos, 0);

  return {
    persons: input.persons
      .map((person) => ({
        key: String(person.id),
        selection: person.id,
        label: person.name,
        videos: person.videos,
        hasCv: person.hasCv,
      }))
      .sort((a, b) => COLLATOR.compare(a.label, b.label)),
    none: {
      key: 'none',
      selection: 'none',
      label: 'Sin persona',
      videos: input.total === null ? null : Math.max(0, input.total - assigned),
      hasCv: false,
    },
  };
}

/** «Ana García · 3 vídeos · con CV»: the entry for a screen reader, which sees no badge. */
export function personAccessibleName(entry: PersonEntry): string {
  const parts = [entry.label];

  if (entry.videos !== null) {
    parts.push(`${formatCount(entry.videos)} ${entry.videos === 1 ? 'vídeo' : 'vídeos'}`);
  }
  if (entry.hasCv) parts.push('con CV');

  return parts.join(' · ');
}

/** The entry the address names, or null: nothing selected, or a person the list does not hold. */
export function findPersonEntry(
  sidebar: PersonSidebar,
  selection: PersonSelection,
): PersonEntry | null {
  if (selection === null) return null;
  if (selection === 'none') return sidebar.none;

  return sidebar.persons.find((entry) => entry.selection === selection) ?? null;
}

/**
 * The address names a person the list, once read, does not have. Never before it is read, nor
 * while it is being read again: a person just created is not in the old answer.
 */
export function personGone(
  persons: readonly Person[] | undefined,
  selection: PersonSelection,
  fetching: boolean,
): boolean {
  if (typeof selection !== 'number' || persons === undefined || fetching) return false;

  return !persons.some((person) => person.id === selection);
}

// ── What the main pane says ─────────────────────────────────────────────────

export interface PersonHeading {
  eyebrow: string;
  title: string;
  description: string | null;
}

/** The title over one person's videos; null when no person is selected. */
export function personHeading(
  selection: PersonSelection,
  entry: PersonEntry | null,
): PersonHeading | null {
  if (selection === null) return null;

  if (selection === 'none') {
    return {
      eyebrow: 'Data',
      title: 'Sin persona',
      description: 'Los vídeos que nadie ha asignado a una persona.',
    };
  }

  // Null is a person still being read: the dataset's own title over their videos would mislead.
  return { eyebrow: 'Persona', title: entry?.label ?? 'Persona', description: null };
}

export interface PersonEmptyState {
  /** Nothing to press: videos reach a person from the gallery, not from an empty one. */
  action: null;
  title: string;
  description: string;
}

/** An empty gallery under a person, with no search nor filter on: not «ningún vídeo casa». */
export function personEmptyState(selection: Exclude<PersonSelection, null>): PersonEmptyState {
  if (selection === 'none') {
    return {
      action: null,
      title: 'Todos los vídeos tienen persona',
      description: 'No queda ninguno sin asignar.',
    };
  }

  return {
    action: null,
    title: 'Esta persona no tiene vídeos',
    description: 'Súbelos con «+», o asígnaselos desde la selección de «Dataset».',
  };
}

// ── Creating and renaming ───────────────────────────────────────────────────

/** The name as it is stored: trimmed, its inner spaces collapsed. */
export function normalisePersonName(name: string): string {
  return name.trim().replace(/\s+/g, ' ');
}

/**
 * Why this name cannot be saved, or null when it can.
 *
 * Said in the form before the API says it less clearly. «Taken» is compared without case, as
 * upstream compares it, and a person's own name is not taken by them (`selfId`): saving a rename
 * that only changes the capitals has to work.
 */
export function personNameProblem(
  name: string,
  persons: readonly Pick<Person, 'id' | 'name'>[],
  selfId: number | null = null,
): string | null {
  const wanted = normalisePersonName(name);

  if (wanted === '') return 'Escribe un nombre';
  if (wanted.length > PERSON_NAME_MAX) return `Como mucho ${PERSON_NAME_MAX} caracteres`;

  const taken = persons.some(
    (person) => person.id !== selfId && person.name.toLowerCase() === wanted.toLowerCase(),
  );

  return taken ? 'Ya hay una persona con ese nombre' : null;
}

// ── Assigning ───────────────────────────────────────────────────────────────

export interface AssignOption {
  /** The radio's value: `none`, or the person's id as text. */
  value: string;
  label: string;
  /** «3 vídeos», or null for «sin persona». */
  detail: string | null;
  hasCv: boolean;
}

export const ASSIGN_NONE = 'none';

/**
 * What «Asignar a persona» offers: «Sin persona» first — the one way to take a person off a
 * video — then everybody, narrowed by what is typed. «Sin persona» is never narrowed away.
 */
export function assignOptions(persons: readonly Person[], search = ''): AssignOption[] {
  const needle = normalisePersonName(search).toLowerCase();

  const people = [...persons]
    .filter((person) => needle === '' || person.name.toLowerCase().includes(needle))
    .sort((a, b) => COLLATOR.compare(a.name, b.name))
    .map((person) => ({
      value: String(person.id),
      label: person.name,
      detail: `${formatCount(person.videos)} ${person.videos === 1 ? 'vídeo' : 'vídeos'}`,
      hasCv: person.hasCv,
    }));

  return [{ value: ASSIGN_NONE, label: 'Sin persona', detail: null, hasCv: false }, ...people];
}

/** The option that is preselected: the person every one of these videos already has, if one. */
export function sharedAssignChoice(videos: readonly { person: { id: number } | null }[]): string {
  const [first, ...rest] = videos;
  if (!first?.person) return '';

  const id = first.person.id;

  return rest.every((video) => video.person?.id === id) ? String(id) : '';
}

/** The person an option names: an id, or null for «sin persona»; undefined for no choice. */
export function assignTarget(choice: string): number | null | undefined {
  if (choice === ASSIGN_NONE) return null;

  return /^\d+$/.test(choice) ? Number(choice) : undefined;
}

/** The ids in requests upstream takes: each once, 500 at most per request. */
export function inAssignBatches(videoIds: readonly number[]): number[][] {
  const unique = [...new Set(videoIds)];
  const batches: number[][] = [];

  for (let start = 0; start < unique.length; start += MAX_VIDEOS_PER_ASSIGN) {
    batches.push(unique.slice(start, start + MAX_VIDEOS_PER_ASSIGN));
  }

  return batches;
}

/**
 * What the toast says once it is done, by what was written and not by what was asked: a video
 * deleted meanwhile is skipped upstream, and claiming it would be claiming work that did not happen.
 */
export function assignedMessage(personName: string | null, updated: number, asked: number): string {
  const videos = `${formatCount(updated)} ${updated === 1 ? 'vídeo' : 'vídeos'}`;
  const done =
    personName === null
      ? `${videos} sin persona`
      : `${videos} ${updated === 1 ? 'asignado' : 'asignados'} a «${personName}»`;

  return updated < asked ? `${done} (de ${formatCount(asked)}: el resto ya no existe)` : done;
}

// ── Dragging videos onto a person ───────────────────────────────────────────

/**
 * A type of its own, apart from the one «Añadir vídeos» drags with: the gallery is that one's drop
 * zone, and a card dragged over its own gallery must not light it up as if it could be dropped
 * there. Lower-case, as browsers report it.
 */
export const PERSON_DRAG_MIME = 'application/x-beonit-vp-person-video-ids';

/** What the sidebar says under a drag: where the videos would go. */
export function personDropHint(entry: PersonEntry): string {
  return entry.selection === 'none'
    ? 'Soltar para dejarlos sin persona'
    : `Asignar a «${entry.label}»`;
}
